// Scoped tweaks to Horizon's native quick-add buttons inside the custom product
// grid sections (Best Sellers, Trending Tabs) and the native product card
// (collection page, product recommendations, search, cart) — without touching
// the global add_to_cart_animation setting or the quick-add snippet itself.
const SCOPES = [
  '.tagged-grid__form',
  '.trending-tabs__card-form',
  '.collection-spotlight__product-form',
  '.product-card__content',
];

/**
 * Builds a selector list where `suffix` is applied to EVERY scope, e.g.
 * scopedSelector('.foo') with SCOPES [A, B] returns "A .foo, B .foo" - not
 * "A, B .foo". Plain string concatenation ("A, B" + " .foo") is a classic
 * trap here: in a comma-separated list, a trailing descendant combinator
 * only binds to the LAST segment, so earlier scopes silently match
 * themselves instead of the intended descendant. That bug is exactly what
 * broke relabeling on Best Sellers/Trending Tabs/Collection spotlight for a
 * while - .tagged-grid__form itself (the form wrapper div, not a button)
 * was what actually got matched and passed to relabelButton, which found no
 * plain text node on it and silently did nothing.
 */
function scopedSelector(suffix) {
  return SCOPES.map((scope) => `${scope} ${suffix}`).join(', ');
}

const ADD_TO_CART_COMPONENT_SELECTOR = scopedSelector('add-to-cart-component');
const ADD_BUTTON_SELECTOR = scopedSelector('.quick-add__button--add');

function disableFlyToCart(component) {
  // Disable the "fly to cart" animation just for these buttons - it doesn't fit
  // a persistent inline button the way it does a floating quick-add pill.
  component.dataset.addToCartAnimation = 'false';
}

function relabelButton(button) {
  if (button.dataset.labelOverridden === 'true') return;

  // Relabel "Add" to "Add to Cart". The quick-add snippet hardcodes the short
  // "Add" translation string with no parameter to override it, so the direct
  // text node is replaced instead.
  for (const node of button.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()) {
      node.textContent = ' Add to Cart ';
      button.dataset.labelOverridden = 'true';
      break;
    }
  }
}

function applyOverrides(root) {
  // querySelectorAll only matches descendants, not `root` itself - cover the
  // case where a mutation adds the target node directly (e.g. morphSection
  // swapping in a component rather than a wrapper around one).
  if (root.matches?.(ADD_TO_CART_COMPONENT_SELECTOR)) disableFlyToCart(root);
  if (root.matches?.(ADD_BUTTON_SELECTOR)) relabelButton(root);

  root.querySelectorAll?.(ADD_TO_CART_COMPONENT_SELECTOR).forEach(disableFlyToCart);
  root.querySelectorAll?.(ADD_BUTTON_SELECTOR).forEach(relabelButton);
}

applyOverrides(document);

// Sections like "You may also like" (product-recommendations) lazy-load their
// product cards via JS well after this script's initial run, so a one-time
// pass at the top of the file misses them entirely. Watch for new nodes being
// added anywhere in the page and re-apply to whatever just arrived.
const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      applyOverrides(node);
    }
  }
});

mutationObserver.observe(document.body, { childList: true, subtree: true });
